import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Heart, Loader2, ShieldCheck } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  full_name: "",
  contact_method: "phone",
  contact_value: "",
  num_children: 1,
  preferred_date: "",
  location_area: "",
  notes: "",
};

export default function RequestHelp() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.contact_value.trim()) {
      toast.error("Please add your name and how to reach you.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/help-request`, {
        ...form,
        num_children: Number(form.num_children) || 0,
      });
      setDone(true);
      setForm(initial);
      toast.success("We've got your request. We'll reach out within 48 hours.");
    } catch (err) {
      console.error(err);
      toast.error("Couldn't submit. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="request"
      data-testid="request-help-section"
      className="py-20 md:py-28 bg-[#F9F7F4]"
    >
      <div className="mx-auto max-w-6xl px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#D85C41]">
              Request Help
            </span>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold leading-[1.05] text-[#1C1917]">
              You don't have to explain. Just ask.
            </h2>
            <p className="mt-5 text-base md:text-lg text-[#57534E] leading-relaxed">
              This form goes to a small, trusted team. We don't ask for ID,
              proof of housing, or income. We'll reach out within 48 hours to
              arrange free pickup, drop-off, or wash-and-fold.
            </p>
            <div className="mt-8 flex items-start gap-3 rounded-2xl bg-[#EAE5DF] p-5">
              <ShieldCheck className="h-5 w-5 text-[#9EA992] flex-shrink-0 mt-0.5" strokeWidth={2.2} />
              <p className="text-sm text-[#57534E] leading-relaxed">
                <strong className="text-[#1C1917]">Your privacy matters.</strong>{" "}
                Your details are seen by 2 program coordinators only. They are
                never shared, sold, or used for marketing.
              </p>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="request-help-form"
            className="lg:col-span-7 rounded-3xl bg-[#F0EFEB] border border-[#E0DCD3] p-7 md:p-10 space-y-5"
          >
            <div>
              <label className="block text-sm font-semibold text-[#1C1917] mb-2">
                Your name
              </label>
              <input
                data-testid="rh-name-input"
                value={form.full_name}
                onChange={(e) => update("full_name", e.target.value)}
                placeholder="First name is fine"
                className="w-full rounded-xl border border-[#D6D3D1] bg-white px-4 py-3 text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:ring-2 focus:ring-[#D85C41]/40 focus:border-[#D85C41]"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold text-[#1C1917] mb-2">
                  Best way to reach you
                </label>
                <Select
                  value={form.contact_method}
                  onValueChange={(v) => update("contact_method", v)}
                >
                  <SelectTrigger
                    data-testid="rh-contact-method-trigger"
                    className="w-full rounded-xl border-[#D6D3D1] bg-white py-6 text-[#1C1917]"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="phone">Phone call</SelectItem>
                    <SelectItem value="text">Text message</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1C1917] mb-2">
                  Phone or email
                </label>
                <input
                  data-testid="rh-contact-value-input"
                  value={form.contact_value}
                  onChange={(e) => update("contact_value", e.target.value)}
                  placeholder="(513) 555-1234 or you@email.com"
                  className="w-full rounded-xl border border-[#D6D3D1] bg-white px-4 py-3 text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:ring-2 focus:ring-[#D85C41]/40 focus:border-[#D85C41]"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <label className="block text-sm font-semibold text-[#1C1917] mb-2">
                  # of children
                </label>
                <input
                  data-testid="rh-num-children-input"
                  type="number"
                  min="0"
                  max="20"
                  value={form.num_children}
                  onChange={(e) => update("num_children", e.target.value)}
                  className="w-full rounded-xl border border-[#D6D3D1] bg-white px-4 py-3 text-[#1C1917] focus:outline-none focus:ring-2 focus:ring-[#D85C41]/40 focus:border-[#D85C41]"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-[#1C1917] mb-2">
                  Preferred date / time (optional)
                </label>
                <input
                  data-testid="rh-date-input"
                  value={form.preferred_date}
                  onChange={(e) => update("preferred_date", e.target.value)}
                  placeholder="e.g. Saturday morning, or 'whenever'"
                  className="w-full rounded-xl border border-[#D6D3D1] bg-white px-4 py-3 text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:ring-2 focus:ring-[#D85C41]/40 focus:border-[#D85C41]"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#1C1917] mb-2">
                Neighborhood (optional)
              </label>
              <input
                data-testid="rh-location-input"
                value={form.location_area}
                onChange={(e) => update("location_area", e.target.value)}
                placeholder="e.g. Downtown Hamilton, Mountain"
                className="w-full rounded-xl border border-[#D6D3D1] bg-white px-4 py-3 text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:ring-2 focus:ring-[#D85C41]/40 focus:border-[#D85C41]"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#1C1917] mb-2">
                Anything we should know? (optional)
              </label>
              <textarea
                data-testid="rh-notes-input"
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                rows={4}
                placeholder="Allergies, special requests, items in the load…"
                className="w-full rounded-xl border border-[#D6D3D1] bg-white px-4 py-3 text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:ring-2 focus:ring-[#D85C41]/40 focus:border-[#D85C41] resize-y"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="rh-submit-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#D85C41] px-7 py-3.5 text-sm font-semibold text-white hover:bg-[#BD4C33] transition-all duration-300 hover:-translate-y-0.5 shadow-md shadow-[#D85C41]/20 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Heart className="h-4 w-4" strokeWidth={2.5} />
                  Send Request
                </>
              )}
            </button>

            {done && (
              <p
                data-testid="rh-success-msg"
                className="text-sm text-[#9EA992] font-semibold"
              >
                ✓ Received. A coordinator will be in touch within 48 hours.
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
